import { auth, db } from '../firebaseConfig';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { collection, doc, getDoc, query, where, setDoc, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { defaultPreferences } from '../constants/defaultPreferences';

function fireSignupWelcomeEmail() {
  try {
    httpsCallable(getFunctions(getApp()), 'sendSignupWelcomeEmail')({}).catch((err) => {
      console.warn('[authService] sendSignupWelcomeEmail:', err?.message || err);
    });
  } catch (e) {
    console.warn('[authService] sendSignupWelcomeEmail callable:', e?.message || e);
  }
}



export const authService = {
  // Check if a username is available (case-insensitive).
  // Returns true = available, false = taken, null = can't check (unauthenticated).
  checkUsernameAvailable: async (username) => {
    if (!username || username.trim().length < 3) return false;
    const normalized = username.trim().toLowerCase();
    try {
      const q = query(collection(db, 'users'), where('usernameLower', '==', normalized));
      const snap = await getDocs(q);
      return snap.empty;
    } catch (e) {
      // Unauthenticated users can't query users collection — that's expected.
      // Return null so the UI shows nothing rather than a false "available".
      if (e?.code === 'permission-denied') return null;
      throw e;
    }
  },

  // Login with email or username and password
  login: async (emailOrUsername, password) => {
    let email = emailOrUsername.trim();

    try {
      // If input has no @, treat it as a username and resolve to email
      if (!email.includes('@')) {
        const usernameQuery = query(
          collection(db, 'users'),
          where('usernameLower', '==', email.toLowerCase())
        );
        const usernameSnap = await getDocs(usernameQuery);
        if (usernameSnap.empty) {
          return { user: null, error: 'No account found with this email/username' };
        }
        email = usernameSnap.docs[0].data().email;
      }

      // Proceed with Firebase Auth login using the email
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Get additional user data from Firestore
      let userData = null;
      
      // First check if user exists in Firestore
      const userRef = doc(db, 'users', userCredential.user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists) {
        // User exists in Firestore, get their data
        userData = userDoc.data();
      } else  {
        throw new Error('User not found');
      }
      
      return { 
        user: {
          ...userCredential.user,
          ...userData
        }, 
        error: null 
      };
    } catch (error) {
      let errorMessage = 'An error occurred during login';
      
      // Handle specific Firebase auth errors
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email/username';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email/username or password';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
      }
      
      return { user: null, error: errorMessage };
    }
  },

  // Register new user
  register: async (email, password, username) => {
    let userCredential = null;
    try {
      // Validate username format (no Firestore needed)
      if (!username || username.trim().length < 3) {
        return { user: null, error: 'Username must be at least 3 characters' };
      }
      if (!/^[a-zA-Z0-9_]+$/.test(username.trim())) {
        return { user: null, error: 'Username can only contain letters, numbers and underscores' };
      }
      const normalizedUsername = username.trim().toLowerCase();

      // Create auth user FIRST so Firestore queries run while authenticated
      userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Now authenticated — check username uniqueness
      const usernameQuery = query(
        collection(db, 'users'),
        where('usernameLower', '==', normalizedUsername)
      );
      const usernameSnap = await getDocs(usernameQuery);
      if (!usernameSnap.empty) {
        // Roll back: delete the auth account we just created
        await userCredential.user.delete();
        return { user: null, error: 'Username is already taken' };
      }

      // Create the Firestore user profile
      const userData = {
        uid: userCredential.user.uid,
        username: username.trim(),
        usernameLower: normalizedUsername,
        email,
        phoneNumber: '',
        profilePhoto: '',
        createdAt: new Date(),
        preferences: defaultPreferences
      };

      const userRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userRef, userData);

      fireSignupWelcomeEmail();

      const updatedUser = await getDoc(userRef);
      
      return { 
        user: {
          ...userCredential.user,
          ...updatedUser.data()
        }, 
        error: null 
      };
    } catch (error) {
      // If auth user was created but Firestore write failed, clean up
      if (userCredential?.user) {
        try { await userCredential.user.delete(); } catch {}
      }
      console.error('Registration error:', error);
      let errorMessage = 'An error occurred during registration';
      
      // Handle specific Firebase auth errors
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'An account already exists with this email';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password is too weak';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection';
          break;
        default:
          // Check for blocked requests
          if (error.message && error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
            errorMessage = 'Request blocked by browser extension. Please disable ad blockers or try incognito mode.';
          } else if (error.message && error.message.includes('Failed to fetch')) {
            errorMessage = 'Network request failed. Please check your internet connection and try again.';
          } else {
            errorMessage = error.message || 'Registration failed';
          }
      }
      
      return { user: null, error: errorMessage };
    }
  },

  // Update username (checks uniqueness, excludes current user's own username)
  updateUsername: async (uid, newUsername) => {
    if (!newUsername || newUsername.trim().length < 3) {
      return { error: 'Username must be at least 3 characters' };
    }
    if (!/^[a-zA-Z0-9_]+$/.test(newUsername.trim())) {
      return { error: 'Username can only contain letters, numbers and underscores' };
    }
    const normalized = newUsername.trim().toLowerCase();
    const q = query(collection(db, 'users'), where('usernameLower', '==', normalized));
    const snap = await getDocs(q);
    // Allow if the only match is the current user
    const takenByOther = snap.docs.some(d => d.id !== uid);
    if (takenByOther) return { error: 'Username is already taken' };
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, { username: newUsername.trim(), usernameLower: normalized }, { merge: true });
    return { error: null };
  },

  // Logout
  logout: async () => {
    try {
      await auth.signOut();
      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  },

  // Reset password using the public, rate-limited backend endpoint
  resetPassword: async (email) => {
    try {
      const response = await fetch('https://us-central1-bet-optimized-fe922.cloudfunctions.net/requestPasswordReset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset email');
      }

      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  },

  // Google Sign-In
  signInWithGoogle: async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      // Check if user exists in Firestore
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      let userData;
      if (userDoc.exists()) {
        // User exists, update last login and any new Google data
        userData = userDoc.data();
        await setDoc(userRef, {
          ...userData,
          lastLogin: new Date(),
          displayName: user.displayName || userData.displayName,
          photoURL: user.photoURL || userData.photoURL,
          provider: 'google'
        }, { merge: true });
      } else {
        // New user, create profile with Google data
        const rawUsername = user.displayName || user.email.split('@')[0];
        userData = {
          uid: user.uid,
          username: rawUsername,
          usernameLower: rawUsername.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          phoneNumber: user.phoneNumber || '',
          createdAt: new Date(),
          lastLogin: new Date(),
          provider: 'google',
          preferences: defaultPreferences
        };
        
        await setDoc(userRef, userData);
        fireSignupWelcomeEmail();
      }
      
      return { 
        user: {
          ...user,
          ...userData
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Google Sign-In error:', error);
      let errorMessage = 'An error occurred during Google Sign-In';
      
      // Handle specific Google Sign-In errors
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in was cancelled';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Popup was blocked by browser. Please allow popups and try again';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please try again later';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'An account already exists with this email using a different sign-in method';
          break;
        default:
          // Check for blocked requests
          if (error.message && error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
            errorMessage = 'Request blocked by browser extension. Please disable ad blockers or try incognito mode.';
          } else if (error.message && error.message.includes('Failed to fetch')) {
            errorMessage = 'Network request failed. Please check your internet connection and try again.';
          } else {
            errorMessage = error.message || 'Google Sign-In failed';
          }
      }
      
      return { user: null, error: errorMessage };
    }
  },
}; 